import { useEffect, useState } from 'react';
import { Button, ConfigProvider, DatePicker, Drawer, Input, InputNumber, Select } from 'antd';
import heIL from 'antd/locale/he_IL';
import dayjs, { type Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import { Money } from '../../components/Money';
import { useCreatePettyCashExpense, usePettyCashCategories } from '../../hooks/usePettyCash';
import { todayIsrael } from '../../utils/dates';

interface Props {
  open: boolean;
  pettyCashId: number | null;
  balance: number;
  onClose: () => void;
}

/** Record an expense. It takes the amount off the balance as soon as it's saved. */
export function ExpenseDrawer({ open, pettyCashId, balance, onClose }: Props) {
  const { data: categories = [] } = usePettyCashCategories(open);
  const create = useCreatePettyCashExpense();

  const [date, setDate] = useState<Dayjs>(() => dayjs(todayIsrael()));
  const [amount, setAmount] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setDate(dayjs(todayIsrael()));
    setAmount(null);
    setCategoryId(null);
    setDescription('');
    setNotes('');
  }, [open]);

  const submit = () => {
    if (pettyCashId == null) return;
    if (amount == null || amount <= 0) return void toast.error('יש להזין סכום חיובי');
    if (amount > balance) return void toast.error('אין מספיק כסף בקופה הקטנה');
    if (categoryId == null) return void toast.error('יש לבחור קטגוריה');
    if (!description.trim()) return void toast.error('יש להזין תיאור');
    create.mutate(
      {
        id: pettyCashId,
        body: {
          movement_date: date.format('YYYY-MM-DD'),
          amount,
          category_id: categoryId,
          description: description.trim(),
          notes: notes.trim() || null,
        },
      },
      {
        onSuccess: (res) => {
          toast.success(`הוצאה מספר ${res.movement_number} נרשמה`);
          onClose();
        },
        onError: (err) => toast.error(err.message || 'שגיאה ברישום ההוצאה'),
      },
    );
  };

  return (
    <Drawer
      title={<span className="text-heading-3-bold text-main-text">הוצאה חדשה</span>}
      placement="left"
      open={open}
      onClose={onClose}
      width={520}
      closable={false}
      styles={{ header: { borderBottom: 'none' } }}
    >
      <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
        <div className="flex flex-col gap-5">
          <div className="bg-blue-bg border border-border rounded p-3 flex items-center justify-between">
            <span className="text-secondary-text">יתרה בקופה</span>
            <span className="font-semibold"><Money amount={balance} /></span>
          </div>

          <Field label="תאריך ההוצאה" required>
            <DatePicker
              value={date}
              onChange={(d) => d && setDate(d)}
              format="DD/MM/YYYY"
              allowClear={false}
              style={{ width: '100%' }}
            />
          </Field>

          <Field label="סכום" required>
            <InputNumber
              value={amount}
              onChange={(v) => setAmount(v == null ? null : Number(v))}
              min={0}
              addonAfter="₪"
              style={{ width: '100%' }}
            />
          </Field>

          <Field label="קטגוריה" required>
            <Select
              value={categoryId ?? undefined}
              onChange={(v) => setCategoryId(v)}
              placeholder="בחר קטגוריה"
              options={categories.filter((c) => c.is_active).map((c) => ({ value: c.id, label: c.name }))}
              style={{ width: '100%' }}
            />
          </Field>

          <Field label="תיאור ההוצאה" required>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              placeholder="למשל: קפה וחלב למשרד"
            />
          </Field>

          <Field label="הערות">
            <Input.TextArea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000} rows={2} />
          </Field>

          <div className="flex justify-end gap-3">
            <Button onClick={onClose}>ביטול</Button>
            <Button type="primary" onClick={submit} loading={create.isPending}>
              שמור הוצאה
            </Button>
          </div>
        </div>
      </ConfigProvider>
    </Drawer>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-small text-main-text mb-1">
        {label}
        {required && <span className="text-error-red"> *</span>}
      </div>
      {children}
    </div>
  );
}
